import React, { createContext, useContext, useState, useEffect, useRef, PropsWithChildren } from 'react';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { useIdentity } from '@/hooks/useIdentity';
import { useSubscription } from '@/contexts/SubscriptionContext';
import type { FeatureToggleUserContext, IFeatureToggleService } from '@/shared/types/feature-toggle';

const FeatureToggleContext = createContext<IFeatureToggleService | undefined>(undefined);

const LD_CLIENT_ID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID as string | undefined;

// Local development flag overrides - these flags are enabled by default for local devs
const DEV_FLAG_OVERRIDES: Record<string, boolean> = {
  'workflow-assistant': true,
};

export function FeatureToggleProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, user } = useIdentity();
  const { platformUserId } = useSubscription();
  const [isReady, setIsReady] = useState(false);
  const clientRef = useRef<LDClient.LDClient | null>(null);

  useEffect(() => {
    if (!LD_CLIENT_ID || !isAuthenticated || !user?.profile || !platformUserId) {
      return;
    }

    const userId = platformUserId;
    const orgId = (user.profile as Record<string, unknown>).org_id as string | undefined;

    const ldContext: LDClient.LDContext = orgId
      ? {
          kind: 'multi',
          user: { key: userId },
          organization: { key: orgId },
        }
      : {
          kind: 'user',
          key: userId,
        };

    const client = LDClient.initialize(LD_CLIENT_ID, ldContext);
    clientRef.current = client;

    client.on('ready', () => {
      setIsReady(true);
    });

    return () => {
      client.close();
      clientRef.current = null;
      setIsReady(false);
    };
  }, [isAuthenticated, platformUserId]);

  const checkFlag = (flagName: string, defaultValue: boolean, _userContext: FeatureToggleUserContext): boolean => {
    // In local development, use dev overrides if defined
    if (import.meta.env.DEV && flagName in DEV_FLAG_OVERRIDES) {
      return DEV_FLAG_OVERRIDES[flagName];
    }

    if (!clientRef.current || !isReady) {
      return defaultValue;
    }

    try {
      return clientRef.current.variation(flagName, defaultValue) as boolean;
    } catch {
      return defaultValue;
    }
  };

  return (
    <FeatureToggleContext.Provider value={{ checkFlag, isReady }}>
      {children}
    </FeatureToggleContext.Provider>
  );
}

export function useFeatureToggle(): IFeatureToggleService {
  const context = useContext(FeatureToggleContext);

  if (!context) {
    throw new Error('useFeatureToggle must be used within FeatureToggleProvider');
  }

  return context;
}
